import { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import DMCAEmailModal from './DMCAEmailModal';
import { useAction } from 'convex/react';
import { decodeUrl } from '../lib/urlUtils';
import './DomainAnalytics.css';

interface DomainAnalyticsProps {
  onNavigateToScan?: () => void;
}

export default function DomainAnalytics({ onNavigateToScan }: DomainAnalyticsProps) {
  const navigate = useNavigate();
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDMCAModal, setShowDMCAModal] = useState(false);
  const resendLast = useAction(api.dmca.resendLastDMCAForDomain);

  const summary = useQuery(api.domainAnalytics.getDomainAnalyticsSummary);
  const topOffenders = useQuery(api.domainAnalytics.getTopRepeatOffenders, { limit: 100 });
  const domainDetails = useQuery(
    api.domainAnalytics.getDomainDetails,
    selectedDomain ? { domain: selectedDomain } : undefined
  );
  const searchResults = useQuery(
    api.domainAnalytics.searchDomains,
    searchTerm.length >= 3 ? { searchTerm, limit: 20 } : undefined
  );

  // Initialize selected domain from global search handoff (must run before any early returns)
  useEffect(() => {
    const preset = localStorage.getItem('domainAnalyticsSelected');
    if (preset) {
      setSelectedDomain(preset);
      localStorage.removeItem('domainAnalyticsSelected');
    }
  }, []);

  // Deduplicate violations by URL, keeping only the most recent one for each unique URL
  const uniqueViolations = useMemo(() => {
    if (!domainDetails?.violations) return [];

    const violationsByUrl = new Map<string, any>();
    for (const violation of domainDetails.violations) {
      const existing = violationsByUrl.get(violation.url);
      // Keep the most recent violation for each URL
      if (!existing || new Date(violation.detectedAt) > new Date(existing.detectedAt)) {
        violationsByUrl.set(violation.url, violation);
      }
    }

    // Return sorted by date (most recent first)
    return Array.from(violationsByUrl.values()).sort(
      (a, b) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    );
  }, [domainDetails?.violations]);

  if (!summary || !topOffenders) {
    return (
      <div className="domain-analytics">
        <div className="loading">Loading domain analytics...</div>
      </div>
    );
  }

  // Empty state - no violations detected yet
  if (summary.totalUniqueDomains === 0) {
    return (
      <div className="domain-analytics">
        <div className="empty-state-page">
          <div className="empty-icon">🎯</div>
          <h2>No Domain Data Yet</h2>
          <p className="empty-description">
            Domain analytics tracks piracy sites that appear across multiple scans, helping you identify repeat offenders.
          </p>
          <p className="empty-hint">
            Run your first scan to start identifying piracy domains and repeat offenders.
          </p>
          {onNavigateToScan && (
            <button className="btn-primary" onClick={onNavigateToScan}>
              🔍 Run Your First Scan
            </button>
          )}
        </div>
      </div>
    );
  }

  const displayDomains = searchTerm.length >= 3 && searchResults ? searchResults : topOffenders;

  return (
    <div className="domain-analytics">

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-value">{summary.totalUniqueDomains}</div>
          <div className="stat-label">Unique Domains</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary.totalViolations}</div>
          <div className="stat-label">Total Violations</div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-value">{summary.repeatOffendersCount}</div>
          <div className="stat-label">Repeat Offenders (2+)</div>
        </div>

        <div className="stat-card critical">
          <div className="stat-value">{summary.serialOffendersCount}</div>
          <div className="stat-label">Serial Offenders (5+)</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary.avgViolationsPerDomain}</div>
          <div className="stat-label">Avg Violations/Domain</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary.recentlyActiveCount}</div>
          <div className="stat-label">Active (Last 7 Days)</div>
        </div>
      </div>

      {/* Top Hosting Providers */}
      {summary.topHostingProviders.length > 0 && (
        <div className="hosting-providers-section">
          <h3>🌐 Top Hosting Providers</h3>
          <div className="providers-grid">
            {summary.topHostingProviders.map(({ provider, count }) => (
              <div key={provider} className="provider-card">
                <span className="provider-name">{provider}</span>
                <span className="provider-count">{count} domains</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="search-section">
        <input
          type="text"
          placeholder="Search domains..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="domain-search"
        />
        {searchTerm.length > 0 && searchTerm.length < 3 && (
          <div className="search-hint">Type at least 3 characters to search</div>
        )}
      </div>

      {/* Domain List */}
      <div className="domains-section">
        <table className="domains-table">
          <thead>
            <tr>
              <th className="col-domain">Domain</th>
              <th className="col-status">Status</th>
              <th className="col-violations">Violations</th>
              <th className="col-content">Content</th>
              <th className="col-hosting">Hosting</th>
              <th className="col-dates">Last Seen</th>
            </tr>
          </thead>

          <tbody>
            {displayDomains.map((domain) => (
              <tr
                key={domain.domain}
                className={`domain-row ${domain.totalViolations >= 5 ? 'serial-offender' : domain.totalViolations >= 2 ? 'repeat-offender' : ''}`}
                onClick={() => setSelectedDomain(domain.domain)}
              >
                <td className="col-domain">
                  <span className="domain-name">{domain.domain}</span>
                </td>

                <td className="col-status">
                  {domain.totalViolations >= 5 && (
                    <span className="badge serial">☠️ Serial Offender</span>
                  )}
                  {domain.totalViolations >= 2 && domain.totalViolations < 5 && (
                    <span className="badge repeat">🔥 Repeat</span>
                  )}
                </td>

                <td className="col-violations">
                  <span className="violation-count">{domain.totalViolations}</span>
                </td>

                <td className="col-content">
                  <span className="content-count">{domain.contentTitles.length} titles</span>
                </td>

                <td className="col-hosting">
                  {domain.hostingProvider ? (
                    <span className="hosting-provider">{domain.hostingProvider}</span>
                  ) : (
                    <span className="no-info">Unknown</span>
                  )}
                </td>

                <td className="col-dates">
                  <span className="date">{new Date(domain.lastSeenAt).toLocaleDateString()}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Domain Details Modal */}
      {selectedDomain && domainDetails && (
        <div className="domain-modal-overlay" onClick={() => setSelectedDomain(null)}>
          <div className="domain-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedDomain}</h3>
              <button onClick={() => setSelectedDomain(null)} className="close-btn">✕</button>
            </div>

            <div className="modal-body">
              <div className="domain-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Violations</span>
                  <span className="stat-value">{domainDetails.analytics.totalViolations}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">Average Confidence</span>
                  <span className="stat-value">{domainDetails.analytics.avgConfidenceScore.toFixed(1)}%</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">First Seen</span>
                  <span className="stat-value">{new Date(domainDetails.analytics.firstSeenAt).toLocaleString()}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">Last Seen</span>
                  <span className="stat-value">{new Date(domainDetails.analytics.lastSeenAt).toLocaleString()}</span>
                </div>

                {domainDetails.analytics.hostingProvider && (
                  <>
                    <div className="stat-item">
                      <span className="stat-label">Hosting Provider</span>
                      <span className="stat-value">{domainDetails.analytics.hostingProvider}</span>
                    </div>

                    <div className="stat-item">
                      <span className="stat-label">IP Address</span>
                      <span className="stat-value">{domainDetails.analytics.ipAddress}</span>
                    </div>

                    <div className="stat-item">
                      <span className="stat-label">Server Location</span>
                      <span className="stat-value">{domainDetails.analytics.serverLocation}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="content-titles">
                <h4>Content Titles ({domainDetails.analytics.contentTitles.length})</h4>
                <div className="titles-list">
                  {domainDetails.analytics.contentTitles.map((title, idx) => (
                    <span key={idx} className="title-tag">{title}</span>
                  ))}
                </div>
              </div>

              <div className="dmca-action-section">
                <button
                  className="btn-dmca"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDMCAModal(true);
                  }}
                >
                  ⚖️ Send DMCA Takedown Notice
                </button>
                <p className="dmca-hint">
                  Generate and send a professional DMCA takedown notice to the hosting provider or domain registrar
                </p>
              </div>

              <div className="violation-history">
                <h4>Violation History ({uniqueViolations.length})</h4>
                <div className="violations-list">
                  {uniqueViolations.map((violation: any) => (
                    <div key={violation._id} className="violation-item">
                      <div className="violation-header">
                        <span className="violation-title">{violation.scan?.contentTitle}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span className="violation-confidence">{violation.confidenceScore}%</span>
                          {violation.scan?.batchId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/scan/${violation.scan.batchId}`);
                              }}
                              style={{
                                fontSize: '0.8rem',
                                color: '#2563eb',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: '0.25rem 0.5rem'
                              }}
                            >
                              View scan →
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="violation-details">
                        <span className="violation-url">{decodeUrl(violation.url)}</span>
                        <span className="violation-date">{new Date(violation.detectedAt).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DMCA Email Modal */}
      {showDMCAModal && selectedDomain && (
        <DMCAEmailModal
          domain={selectedDomain}
          onClose={() => setShowDMCAModal(false)}
        />
      )}

      {/* Resend last DMCA for selected domain (footer action) */}
      {selectedDomain && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn-secondary"
            onClick={async () => {
              try {
                await resendLast({ domain: selectedDomain });
                alert('Resent latest DMCA email for ' + selectedDomain);
              } catch (e: any) {
                alert('Failed to resend DMCA: ' + (e?.message || 'unknown error'));
              }
            }}
          >
            ↻ Resend Last DMCA
          </button>
        </div>
      )}
    </div>
  );
}
